import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Loader2, ShieldAlert, CheckCircle2, XCircle } from "lucide-react";

function statusBadge(status) {
  const map = {
    COMPLETED: { className: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Bezahlt" },
    CREATED: { className: "bg-amber-100 text-amber-800 border-amber-200", label: "Offen" },
    REFUNDED: { className: "bg-slate-100 text-slate-700 border-slate-200", label: "Erstattet" },
  };
  const s = map[status] || { className: "bg-slate-100 text-slate-700", label: status || "-" };
  return <Badge variant="outline" className={`${s.className} rounded-full font-medium`}>{s.label}</Badge>;
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function RefundButton({ order, onRefunded }) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const doRefund = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/refund", { order_id: order.id });
      if (data.status === "REFUNDED") {
        toast.success(`Refund erfolgreich (${data.refund_id})`);
      } else if (data.status === "already_refunded") {
        toast.info("Bereits erstattet.");
      } else {
        toast.info(`Status: ${data.status}`);
      }
      onRefunded();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Refund fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const disabled = order.status !== "COMPLETED";

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="rounded-full border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 disabled:opacity-40"
          data-testid={`refund-btn-${order.id}`}
        >
          Erstatten
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-600" /> Zahlung erstatten?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p>Diese Aktion erstattet die Zahlung <strong>{order.amount} € an {order.user_email || order.user_id}</strong> vollständig über PayPal und entzieht den Ernährungsplan-Zugang. Das kann nicht rückgängig gemacht werden.</p>
              <p className="font-mono text-xs text-slate-500">Order: {order.id}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            onClick={doRefund}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            data-testid={`refund-confirm-${order.id}`}
          >
            {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird erstattet...</>) : "Ja, erstatten"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function Admin() {
  const [orders, setOrders] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setRefreshing(true);
    setError("");
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Bestellungen konnten nicht geladen werden.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = React.useMemo(() => {
    if (!Array.isArray(orders)) return { total: 0, paid: 0, refunded: 0, revenue: 0 };
    const paid = orders.filter((o) => o.status === "COMPLETED");
    const refunded = orders.filter((o) => o.status === "REFUNDED");
    const revenue = paid.reduce((sum, o) => sum + parseFloat(o.amount || 0), 0);
    return { total: orders.length, paid: paid.length, refunded: refunded.length, revenue };
  }, [orders]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-2">Admin</div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Bestellungen & Refunds</h1>
        </div>
        <Button onClick={load} disabled={refreshing} variant="outline" className="rounded-full btn-lift" data-testid="admin-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Aktualisieren
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card className="p-5 rounded-2xl border-slate-100">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Gesamt</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</div>
        </Card>
        <Card className="p-5 rounded-2xl border-emerald-100 bg-emerald-50/40">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Bezahlt</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.paid}</div>
        </Card>
        <Card className="p-5 rounded-2xl border-slate-100">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Erstattet</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.refunded}</div>
        </Card>
        <Card className="p-5 rounded-2xl border-slate-100">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Netto-Umsatz</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.revenue.toFixed(2).replace(".", ",")} €</div>
        </Card>
      </div>

      <Card className="p-0 rounded-2xl border-slate-100 overflow-hidden" data-testid="admin-orders-table">
        {orders === null ? (
          <div className="p-10 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Bestellungen werden geladen...
          </div>
        ) : error ? (
          <div className="p-10 text-center text-red-600 text-sm">{error}</div>
        ) : orders.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">Noch keine Bestellungen vorhanden.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wide">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Order-ID</th>
                  <th className="text-left px-6 py-3 font-semibold">Kunde</th>
                  <th className="text-left px-6 py-3 font-semibold">Betrag</th>
                  <th className="text-left px-6 py-3 font-semibold">Status</th>
                  <th className="text-left px-6 py-3 font-semibold">Erstellt</th>
                  <th className="text-left px-6 py-3 font-semibold">Erstattet</th>
                  <th className="text-right px-6 py-3 font-semibold">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50/60" data-testid={`order-row-${o.id}`}>
                    <td className="px-6 py-4 font-mono text-xs text-slate-600 max-w-[220px] truncate" title={o.id}>{o.id}{o.mock && <span className="ml-2 text-amber-600">(Mock)</span>}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-900">{o.user_email || "-"}</div>
                      <div className="text-xs text-slate-500 font-mono">{o.user_id?.slice(0, 8)}</div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900">{o.amount ? `${String(o.amount).replace(".", ",")} €` : "-"}</td>
                    <td className="px-6 py-4">{statusBadge(o.status)}</td>
                    <td className="px-6 py-4 text-slate-600 text-xs">{formatDate(o.created_at)}</td>
                    <td className="px-6 py-4 text-slate-600 text-xs">{formatDate(o.refunded_at)}</td>
                    <td className="px-6 py-4 text-right">
                      <RefundButton order={o} onRefunded={load} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
